import type { AssistantSuggestion } from '~/types/models';

export type AssistantSuggestionRefinementInput = {
    instructions: string;
    projectId?: string;
};

export type AssistantSuggestionApplyInput = {
    destination?: 'default' | 'new_project' | 'existing_project';
    projectId?: string;
    selectedTaskIds?: string[];
    dismissedTaskIds?: string[];
    leftoverAction?: 'keep' | 'dismiss';
    /** Stable analysis or prompt turn key used to make confirmed writes idempotent. */
    contextKey?: string;
};

export function assistantSuggestionCreatesTask(kind: AssistantSuggestion['kind']): boolean {
    return ['new_task', 'follow_up', 'quote_review', 'risk_flag'].includes(kind);
}

export function assistantSuggestionCardDismissals(
    primary: AssistantSuggestion,
    groupedTasks: AssistantSuggestion[] = [],
): AssistantSuggestion[] {
    const suggestions = primary.kind === 'reply_draft'
        ? [primary]
        : [primary, ...groupedTasks];
    const seen = new Set<string>();

    return suggestions.filter((suggestion) => {
        if (seen.has(suggestion.id)) return false;
        seen.add(suggestion.id);
        return true;
    });
}

export function assistantActionLabel(suggestion: AssistantSuggestion | null): string {
    if (!suggestion) return 'No Action Needed';
    if (suggestion.kind === 'new_project' || suggestion.kind === 'new_task') return 'Action Needed';
    if (suggestion.kind === 'quote_review') return 'Follow-up Needed';
    if (suggestion.kind === 'risk_flag') return 'Project Risk';
    if (suggestion.kind === 'follow_up') return 'Follow-up Needed';
    return 'Informational';
}

export function assistantActionBadgeClass(suggestion: AssistantSuggestion | null): string {
    if (!suggestion) return 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200';
    if (suggestion.kind === 'new_project' || suggestion.kind === 'new_task') return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200';
    if (suggestion.kind === 'quote_review' || suggestion.kind === 'follow_up') return 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200';
    if (suggestion.kind === 'risk_flag') return 'border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200';
    return 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200';
}

export function assistantConfidenceBadgeClass(confidence?: string): string {
    if (confidence === 'high') return 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200';
    if (confidence === 'medium') return 'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-200';
    return 'border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200';
}

export function assistantPriorityLabel(confidence?: string): string {
    if (confidence === 'high') return 'High confidence';
    if (confidence === 'medium') return 'Medium confidence';
    return 'Low confidence';
}
